import { omitBy } from "lodash";
import {
  BenefitsPayload,
  Pillar,
  Pillars,
  PillarPayload,
  PillarsPayload,
  ScoresPayload,
  SolutionPayload,
} from "../types";
import { sanitiseText } from "../formatting/sanitise-rich-text";
import {
  normaliseInterventions,
  normaliseOutcomes,
} from "../formatting/domain-value-manipulation";

const isEmptyPayloadValue = (field: unknown) =>
  field === null ||
  field === undefined ||
  field === "" ||
  (typeof field === "object" && Object.keys(field).length === 0);

export const removeNonValues = (payload: SolutionPayload) => {
  const date = omitBy(payload.date, (field) => !field);
  const coordinates =
    payload.coordinates?.latitude && payload.coordinates?.longitude
      ? payload.coordinates
      : null;
  const interventions = normaliseInterventions(payload);
  const outcomes = normaliseOutcomes(payload);
  const programmeInfo = Object.fromEntries(
    Object.entries(payload.programmeInfo || {}).filter(
      ([, value]) =>
        (typeof value === "string" && value) ||
        (Array.isArray(value) && value.length),
    ),
  );
  const internalUseOnly = Object.fromEntries(
    Object.entries(payload.internalUseOnly || {}).filter(([, value]) => value),
  );
  const nestedValues = {
    ...payload,
    coordinates,
    date,
    programmeInfo,
    internalUseOnly,
  };
  const topLevelValues = omitBy(nestedValues, isEmptyPayloadValue);

  return { ...topLevelValues, interventions, outcomes };
};

export function countWords(text: string): number {
  const normalisedText = text.replace(/\u00a0/g, " ").trim();
  if (!normalisedText) return 0;
  return normalisedText.split(/\s+/u).length;
}

export function countHtmlTextWords(html: string): number {
  const sanitisedHtml = sanitiseText(html);
  if (!sanitisedHtml) return 0;

  const document = new DOMParser().parseFromString(sanitisedHtml, "text/html");
  document.body
    .querySelectorAll("br")
    .forEach((element) => element.replaceWith(" "));
  document.body
    .querySelectorAll("p, li")
    .forEach((element) => element.after(" "));

  return countWords(document.body.textContent ?? "");
}

const normalisePillar = (pillar: Pillar | undefined): Pillar | undefined => {
  if (!pillar) return undefined;

  const total = pillar.scores?.total;
  const scores = total !== undefined && total !== null ? { total } : undefined;

  const quantity = pillar.benefits?.quantity?.trim();
  const description = pillar.benefits?.description?.trim();
  const benefits =
    quantity || description
      ? {
          ...(quantity ? { quantity } : {}),
          ...(description ? { description } : {}),
        }
      : undefined;

  if (!scores && !benefits) return undefined;

  return {
    ...(scores ? { scores } : {}),
    ...(benefits ? { benefits } : {}),
  };
};

export const normalisePillars = (
  pillars: Pillars | undefined,
): Pillars | undefined => {
  if (!pillars) return undefined;

  const carbon = normalisePillar(pillars.carbon);
  const biodiversity = normalisePillar(pillars.biodiversity);
  const people = normalisePillar(pillars.people);

  if (!carbon && !biodiversity && !people) return undefined;

  return {
    ...(carbon ? { carbon } : {}),
    ...(biodiversity ? { biodiversity } : {}),
    ...(people ? { people } : {}),
  };
};

const normaliseScoresForUpdate = (
  scores: Pillar["scores"],
): ScoresPayload | undefined => {
  if (!scores) return undefined;

  const { total } = scores;
  return { total: total ?? null };
};

const normaliseBenefitsForUpdate = (
  benefits: Pillar["benefits"],
): BenefitsPayload | undefined => {
  if (!benefits) return undefined;

  const quantity = benefits.quantity?.trim();
  const description = benefits.description?.trim();
  return {
    quantity: quantity || null,
    description: description || null,
  };
};

const normalisePillarForUpdate = (
  pillar: Pillar | undefined,
): PillarPayload | undefined => {
  if (!pillar) return undefined;

  const scores = normaliseScoresForUpdate(pillar.scores);
  const benefits = normaliseBenefitsForUpdate(pillar.benefits);

  if (!scores && !benefits) return undefined;

  return {
    ...(scores ? { scores } : {}),
    ...(benefits ? { benefits } : {}),
  };
};

export const normalisePillarsForUpdate = (
  pillars: Pillars | undefined,
): PillarsPayload | undefined => {
  if (!pillars) return undefined;

  const carbon = normalisePillarForUpdate(pillars.carbon);
  const biodiversity = normalisePillarForUpdate(pillars.biodiversity);
  const people = normalisePillarForUpdate(pillars.people);

  if (!carbon && !biodiversity && !people) return undefined;

  return {
    ...(carbon ? { carbon } : {}),
    ...(biodiversity ? { biodiversity } : {}),
    ...(people ? { people } : {}),
  };
};
